import fs from 'node:fs'
import path from 'node:path'
import { DEFAULT_ADMIN_NAMESPACE, validateAdminNamespace } from '@next-adapter/config/namespace.js'

export type LinterType = 'eslint' | 'biome' | 'none'

export interface LinterInfo {
  /** Detected linter type */
  type: LinterType
  /** Config file found (if any) */
  configFile: string | null
}

export interface ProjectInfo {
  /** Whether this is an existing Next.js project */
  isExisting: boolean
  /** Whether the project uses a src/ directory */
  hasSrcDir: boolean
  /** Whether TypeScript is configured */
  hasTypeScript: boolean
  /** Whether Tailwind CSS is configured */
  hasTailwind: boolean
  /** Detected linter info */
  linter: LinterInfo
  /** Conflicts found that would block init */
  conflicts: string[]
}

const NEXT_CONFIG_FILES = ['next.config.ts', 'next.config.js', 'next.config.mjs']

export function detectProjectName(cwd: string): string {
  const pkgPath = path.join(cwd, 'package.json')
  if (fs.existsSync(pkgPath)) {
    try {
      const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'))
      if (typeof pkg.name === 'string' && pkg.name.length > 0) {
        return formatProjectName(pkg.name)
      }
    } catch {
      /* ignore */
    }
  }
  return formatProjectName(path.basename(cwd))
}

function formatProjectName(name: string): string {
  const base = name.includes('/') ? name.split('/').pop()! : name
  return base
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export function detectProject(cwd: string, namespaceValue = DEFAULT_ADMIN_NAMESPACE): ProjectInfo {
  const namespace = validateAdminNamespace(namespaceValue)
  const isExisting = NEXT_CONFIG_FILES.some((f) => fs.existsSync(path.join(cwd, f)))

  const hasSrcDir = fs.existsSync(path.join(cwd, 'src'))

  const hasTypeScript =
    fs.existsSync(path.join(cwd, 'tsconfig.json')) ||
    fs.existsSync(path.join(cwd, 'tsconfig.app.json'))

  const hasTailwind = detectTailwind(cwd)
  const linter = detectLinter(cwd)

  const conflicts: string[] = []
  if (isExisting) {
    if (fs.existsSync(path.join(cwd, namespace))) {
      conflicts.push(`${namespace}/ directory already exists`)
    }
    if (fs.existsSync(path.join(cwd, 'admin.config.ts'))) {
      conflicts.push('admin.config.ts already exists')
    }

    const appBase = hasSrcDir ? 'src/app' : 'app'
    if (fs.existsSync(path.join(cwd, appBase, `(${namespace})`))) {
      conflicts.push(`${appBase}/(${namespace})/ route group already exists`)
    }

    if (hasTsconfigAdminAliases(cwd, namespace)) {
      conflicts.push(`@${namespace}/* path aliases already exist in tsconfig.json`)
    }
  }

  return { isExisting, hasSrcDir, hasTypeScript, hasTailwind, linter, conflicts }
}

// ============================================================================
// Linter Detection
// ============================================================================

const BIOME_CONFIG_FILES = ['biome.json', 'biome.jsonc']

const ESLINT_CONFIG_FILES = [
  'eslint.config.js',
  'eslint.config.mjs',
  'eslint.config.cjs',
  'eslint.config.ts',
  '.eslintrc.json',
  '.eslintrc.js',
  '.eslintrc.cjs',
  '.eslintrc.yml',
  '.eslintrc.yaml',
  '.eslintrc'
]

export function detectLinter(cwd: string): LinterInfo {
  // Check for Biome first (preferred)
  for (const f of BIOME_CONFIG_FILES) {
    if (fs.existsSync(path.join(cwd, f))) {
      return { type: 'biome', configFile: f }
    }
  }

  // Check for ESLint config files
  for (const f of ESLINT_CONFIG_FILES) {
    if (fs.existsSync(path.join(cwd, f))) {
      return { type: 'eslint', configFile: f }
    }
  }

  // Check package.json for eslintConfig field
  const pkgPath = path.join(cwd, 'package.json')
  if (fs.existsSync(pkgPath)) {
    try {
      const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'))
      if (pkg.eslintConfig) {
        return { type: 'eslint', configFile: 'package.json (eslintConfig)' }
      }
    } catch {
      // ignore parse errors
    }
  }

  return { type: 'none', configFile: null }
}

// ============================================================================
// Tailwind Detection
// ============================================================================

function detectTailwind(cwd: string): boolean {
  // Tailwind v4: check for CSS files with @import "tailwindcss" or @theme
  const cssFiles = ['globals.css', 'app.css', 'index.css'].flatMap((f) => [
    path.join(cwd, 'src', 'app', f),
    path.join(cwd, 'app', f),
    path.join(cwd, 'src', f),
    path.join(cwd, f)
  ])

  for (const cssFile of cssFiles) {
    if (fs.existsSync(cssFile)) {
      const content = fs.readFileSync(cssFile, 'utf-8')
      if (
        content.includes('@import "tailwindcss"') ||
        content.includes("@import 'tailwindcss'") ||
        content.includes('@theme')
      ) {
        return true
      }
    }
  }

  // Also check postcss.config for tailwindcss plugin (v3 style)
  const postcssFiles = ['postcss.config.js', 'postcss.config.mjs', 'postcss.config.cjs']
  for (const f of postcssFiles) {
    if (fs.existsSync(path.join(cwd, f))) {
      const content = fs.readFileSync(path.join(cwd, f), 'utf-8')
      if (content.includes('tailwindcss') || content.includes('@tailwindcss')) {
        return true
      }
    }
  }

  return false
}

function hasTsconfigAdminAliases(cwd: string, namespace: string): boolean {
  const tsconfigPath = path.join(cwd, 'tsconfig.json')
  if (!fs.existsSync(tsconfigPath)) return false

  try {
    const content = fs.readFileSync(tsconfigPath, 'utf-8')
    return content.includes(`@${namespace}/`)
  } catch {
    return false
  }
}

// ============================================================================
// Next.js Version Detection
// ============================================================================

/**
 * Detect the Next.js major version from the project's package.json.
 * Checks dependencies and devDependencies for the `next` package.
 * Returns the major version number (e.g. 15, 16), or 16 as default.
 */
export function detectNextMajorVersion(cwd: string): number {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) return 16

  try {
    const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'))
    const version = pkg.dependencies?.next ?? pkg.devDependencies?.next
    if (typeof version !== 'string') return 16

    // Strip range prefixes (^, ~, >=, etc.) and extract major version
    const match = version.match(/(\d+)/)
    if (!match) return 16

    return Number.parseInt(match[1], 10)
  } catch {
    return 16
  }
}
